import type { MouseEventHandler } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import Caret from 'components/Common/Caret'
import Clickable from 'components/Common/Clickable'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { generateDurations } from 'selectors/offerPage/bedbankOfferPageSelectors'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import useHasMounted from 'hooks/useHasMounted'
import { useIntl } from 'react-intl'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  getElementLogicalInlineStartOffset,
  getLogicalScrollLeft,
  scrollToLogicalPosition,
} from 'LuxKit/Carousel/utils'
import { DURATION_CAROUSEL_PROPS } from 'components/OfferPage/Filters/DurationTabs/durationCarouselLayout'
import {
  durationTabRebrandCaretStyles,
  durationTabRebrandOptionStyles,
} from 'components/OfferPage/Filters/DurationTabs/durationTabRebrandStyles'

const DurationFromText = styled(BodyText)`
  transition: color 0.2s;
`

const DurationCaret = styled(Caret)`
  position: absolute;
  bottom: 0;
  inset-inline-start: 50%;
  transform: translateY(100%) translateX(-50%);
  opacity: 0;
  transition: opactiy 0.2s, border-color 0.2s;
  border-top-color: ${props => props.theme.palette.brand.primary.normal};
  border-width: ${rem(8)};

  [dir='rtl'] & {
    transform: translateY(100%) translateX(50%);
  }

  &.visible {
    opacity: 1;
  }

  ${durationTabRebrandCaretStyles}
`

const DurationOption = styled(Clickable)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: color 0.2s, background-color 0.2s;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  position: relative;
  flex-shrink: 0;
  min-width: ${rem(120)};
  align-self: flex-end;

  &.selected {
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
    background-color: ${props => props.theme.palette.brand.primary.normal};

    ${mediaHoverable} {
      &:hover {
        color: ${props => props.theme.palette.neutral.surface.darkContrast};
        background-color: ${props => props.theme.palette.brand.primary.hover};

        ${DurationCaret} {
          border-top-color: ${props => props.theme.palette.brand.primary.hover};
        }
      }
    }
    &:focus {
      color: ${props => props.theme.palette.neutral.surface.darkContrast};
      background-color: ${props => props.theme.palette.brand.primary.hover};

      ${DurationCaret} {
        border-top-color: ${props => props.theme.palette.brand.primary.hover};
      }
    }
  }

  &:hover, &:focus {
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
    background-color: ${props => props.theme.palette.brand.primary.normal};

    ${DurationFromText} {
      color: ${props => props.theme.palette.neutral.surface.darkContrast};
    }
  }

  ${durationTabRebrandOptionStyles(DurationCaret)}

`

const DurationOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(12)};
`

const PromoTag = styled(Caption)`
  padding: 0 ${rem(4)};
  background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
`

const OfferDurations = styled.div`
  ${mediaQueryUp.desktop} {
    &.centered {
      display: flex;
      justify-content: center;
    }
  }
`

function scrollTabIntoView(carouselElement: HTMLDivElement | null, tabElement: HTMLButtonElement | undefined, behavior?: ScrollBehavior) {
  if (carouselElement && tabElement) {
    scrollToLogicalPosition(carouselElement, {
      left: getElementLogicalInlineStartOffset(tabElement, carouselElement),
      behavior,
    })
  }
}

interface Props {
  checkIn?: string
  checkOut?: string
  selectedDuration: number
  durationOptions: Array<App.BedbankDurationOption>
  className?: string
}

function BedbankDurationTabs(props: Props) {
  const { className, selectedDuration, checkIn, checkOut, durationOptions } = props
  const intl = useIntl()
  const hasMounted = useHasMounted()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const carouselRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Record<string, HTMLButtonElement>>({})
  const offerPageDispatch = useOfferPageDispatch()

  const onDurationClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const duration = parseInt(event.currentTarget.dataset.duration!, 10)

    if (checkIn && checkOut) {
      const newCheckout = moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT)
      offerPageDispatch({ type: OfferPageActions.setCheckOutDate, checkOut: newCheckout })
    }

    offerPageDispatch({ type: OfferPageActions.setDuration, duration })
  }, [offerPageDispatch, checkIn, checkOut])

  useEffect(() => {
    const tabElement = tabRefs.current[selectedDuration]
    const parentElement = tabElement?.parentElement
    // ensure the selected duration is always visible in the carousel
    if (tabElement && parentElement) {
      const startElementPx = getElementLogicalInlineStartOffset(tabElement, parentElement)
      const endElementPx = startElementPx + tabElement.offsetWidth
      const startParentPx = getLogicalScrollLeft(parentElement)
      const endParentPx = parentElement.offsetWidth + startParentPx

      // check if our elements bounds are outside of the currently visible bounds on the scroll parent
      const isNotVisible = startElementPx > endParentPx || endElementPx < startParentPx
      if (isNotVisible) {
        scrollToLogicalPosition(parentElement, { left: startElementPx })
      }
    }
  }, [selectedDuration])

  useEffect(() => {
    if (!hasMounted) {
      return
    }

    const selectedOption = durationOptions.find(option => option.duration === selectedDuration)

    // Ensure promo 'SPECIAL' tags are prominent when a bedbank has promotions
    if (selectedOption) {
      const anchoringTab = isMobileScreen ? tabRefs.current[selectedOption.duration] : tabRefs.current[selectedOption.duration - 2]
      scrollTabIntoView(carouselRef.current, anchoringTab, 'smooth')

      if (checkIn && checkOut && !selectedOption.hasPromo) {
        scrollTabIntoView(carouselRef.current, tabRefs.current[selectedOption.duration], 'smooth')
      }
    } else {
      const firstPromoOption = durationOptions.find(option => option.hasPromo)
      if (firstPromoOption) {
        const anchoringTab = isMobileScreen ? tabRefs.current[firstPromoOption.duration] : tabRefs.current[firstPromoOption.duration - 2]
        scrollTabIntoView(carouselRef.current, anchoringTab, 'smooth')
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMounted])

  return (
    <OfferDurations className={clsx(className, { centered: durationOptions.length < 8 })}>
      <CardCarousel
        ref={carouselRef}
        gap={12}
        {...DURATION_CAROUSEL_PROPS}
      >
        {durationOptions.map((option, index) => {
          const selected = option.duration === selectedDuration
          const hasPromo = option.hasPromo

          return <DurationOption
            key={index}
            className={clsx({ selected })}
            data-testid={`nights-${option.duration}`}
            data-duration={option.duration}
            onClick={onDurationClick}
            ref={(ref: HTMLButtonElement) => {
              if (ref) {
                tabRefs.current[option.duration] = ref
              }
            }}
          >
            {hasPromo && <PromoTag
              align="center"
              colour="normal-contrast"
              weight="bold"
              variant="medium"
              wrap="no-wrap"
            >
              {intl.formatMessage({
                id: 'expGV8AK02RetvDh4pe7',
                defaultMessage: 'Special',
                description: 'Promotional tag label on bedbank duration tab indicating a special deal',
              })}
            </PromoTag>}
            <DurationOptionContent>
              <BodyText variant="medium" weight="bold" wrap="no-wrap">
                {intl.formatMessage({
                  id: 'gVc5A0Ca5W6d1wH5HM1C',
                  defaultMessage: '{count, plural, one {# night} other {# nights}}',
                  description: 'Number of nights label on bedbank duration tab on the offer detail page',
                }, { count: option.duration })}
              </BodyText>
              <DurationCaret className={clsx({ visible: selected })} />
            </DurationOptionContent>
          </DurationOption>
        })}
      </CardCarousel>
    </OfferDurations>

  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  selectedDuration: (state.checkInDate && state.checkOutDate) ? moment(state.checkOutDate).diff(moment(state.checkInDate), 'days') : state.duration,
  durationOptions: generateDurations(state, appState),
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  offer: state.bedbankOffer,
}), BedbankDurationTabs)
